
 // Tracking: Google Consent Mode v2 + GTM or GA4/Google Ads (gtag) + optional Meta Pixel.
//
// CRA environment variables:
// REACT_APP_GTM_ID
// REACT_APP_GA4_ID
// REACT_APP_GADS_ID
// REACT_APP_GADS_LEAD_LABEL
// REACT_APP_META_PIXEL_ID

const GTM_ID = process.env.REACT_APP_GTM_ID;
const GA4_ID = process.env.REACT_APP_GA4_ID;
const GADS_ID = process.env.REACT_APP_GADS_ID;
const GADS_LEAD_LABEL = process.env.REACT_APP_GADS_LEAD_LABEL;
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

const CONSENT_KEY = "onbbits_consent";
const CONSENT_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 182;

let initialised = false;

function loadScript(src) {
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function gtag(...args) {
  window.dataLayer.push(args);
}

export function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;

    const { choice, at } = JSON.parse(raw);

    if (Date.now() - at > CONSENT_MAX_AGE_MS) return null;

    return choice === "granted" || choice === "necessary"
      ? choice
      : null;
  } catch {
    return null;
  }
}

export function initTracking() {
  if (initialised) return;

  initialised = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || gtag;

  const stored = getStoredConsent();
  const granted = stored === "granted" ? "granted" : "denied";

  // Consent Mode v2 defaults must be set before Google tags load.
  window.gtag("consent", "default", {
    ad_storage: granted,
    analytics_storage: granted,
    ad_user_data: granted,
    ad_personalization: granted,
    wait_for_update: 500,
  });

  if (GTM_ID) {
    window.dataLayer.push({
      "gtm.start": Date.now(),
      event: "gtm.js",
    });

    loadScript(
      `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
    );
  } else if (GA4_ID || GADS_ID) {
    loadScript(
      `https://www.googletagmanager.com/gtag/js?id=${GA4_ID || GADS_ID}`
    );

    window.gtag("js", new Date());

    // Page views are sent manually on SPA route changes.
    if (GA4_ID) {
      window.gtag("config", GA4_ID, {
        send_page_view: false,
      });
    }

    if (GADS_ID) {
      window.gtag("config", GADS_ID);
    }
  }

  if (stored === "granted") {
    loadMetaPixel();
  }
}

export function setConsent(choice) {
  if (choice !== "granted" && choice !== "necessary") {
    return;
  }

  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({
        choice,
        at: Date.now(),
      })
    );
  } catch {
    // Storage blocked: the choice applies to this page view only.
  }

  const value = choice === "granted" ? "granted" : "denied";

  window.gtag?.("consent", "update", {
    ad_storage: value,
    analytics_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });

  if (choice === "granted") {
    loadMetaPixel();
  }
}

function loadMetaPixel() {
  if (!META_PIXEL_ID || window.fbq) return;

  const fbq = function (...args) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  };

  fbq.queue = [];

  window.fbq = fbq;
  window._fbq = fbq;

  loadScript("https://connect.facebook.net/en_US/fbevents.js");

  fbq("init", META_PIXEL_ID);
  fbq("track", "PageView");
}

export function trackPageView(path, title) {
  window.dataLayer?.push({
    event: "page_view",
    page_path: path,
    page_title: title,
  });

  if (!GTM_ID && GA4_ID) {
    window.gtag?.("event", "page_view", {
      page_path: path,
      page_title: title,
      page_location: window.location.href,
    });
  }
}

export function trackEvent(name, params = {}) {
  window.dataLayer?.push({
    event: name,
    ...params,
  });

  if (!GTM_ID && (GA4_ID || GADS_ID)) {
    window.gtag?.("event", name, params);
  }
}

/**
 * Primary conversion.
 * Call only after the lead endpoint confirms successful submission.
 */
export function trackLead({
  leadId,
  lpId,
  value,
  email,
  phoneE164,
}) {
  return new Promise((resolve) => {
    let done = false;

    const finish = () => {
      if (!done) {
        done = true;
        resolve();
      }
    };

    setTimeout(finish, 1000);

    window.dataLayer?.push({
      event: "generate_lead",
      transaction_id: leadId,
      lp_id: lpId,
      value,
      currency: "INR",
      user_data: {
        email,
        phone_number: phoneE164,
      },
      ...(GTM_ID
        ? {
            eventCallback: finish,
            eventTimeout: 1000,
          }
        : {}),
    });

    if (!GTM_ID) {
      if (GA4_ID || GADS_ID) {
        window.gtag?.("set", "user_data", {
          email,
          phone_number: phoneE164,
        });

        window.gtag?.("event", "generate_lead", {
          transaction_id: leadId,
          lp_id: lpId,
          value,
          currency: "INR",
          event_callback: finish,
        });
      }

      if (GADS_ID && GADS_LEAD_LABEL) {
        window.gtag?.("event", "conversion", {
          send_to: `${GADS_ID}/${GADS_LEAD_LABEL}`,
          transaction_id: leadId,
          value,
          currency: "INR",
        });
      }
    }

    window.fbq?.(
      "track",
      "Lead",
      { content_name: lpId },
      { eventID: leadId }
    );

    if (!GTM_ID && !GA4_ID && !GADS_ID) {
      finish();
    }
  });
}
